import { z } from "zod";
import validator from "validator";
import { isZipCodeValidForState } from "helpers/stateZipCodeMapping";
import { noSpecialCharactersRegex } from "./noSpecialCharactersRegex";

const HasUsdot = z.preprocess(
  (v) => (v === "" || v == null ? undefined : v),
  z.enum(["yes", "no"], {
    invalid_type_error: "Please select Yes or No",
  }).optional()
);

// const DotNumber = z.preprocess((v) => {
//   if (v === "" || v == null) return undefined;
//   if (typeof v === "string") {
//     const n = Number(v);
//     return Number.isNaN(n) ? v : n;
//   }
//   return v;
// }, z.number({ invalid_type_error: "DOT Number must be a number" }).int("DOT Number must be an integer").positive("DOT Number must be positive").min(1, "DOT Number must be at least 1").max(999_999_999, "DOT Number must be at most 999999999").optional());

const DotNumber = z.preprocess((v) => {
  if (v === "" || v == null) return undefined;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
  }
  return v;
}, z.number().int("DOT Number must be an integer").positive("DOT Number must be positive").min(1, "DOT Number must be at least 1").max(999_999_999, "DOT Number must be at most 999999999").optional());
// .superRefine(
//   (val, ctx) => {
//     console.log('Checking', ctx)
//     const hasUsdot = ctx?.parent?.hasUsdot;  // 👈 pull sibling field
//     if (hasUsdot === "yes" && val == null) {
//       return false;
//     }
//     if (hasUsdot === "no" && val != null) {
//       return false;
//     }
//     return true;
//   },
//   {
//     message: "DOT Number must be present only if Has USDOT is Yes",
//   }
// );

const emptyToUndef = (v) =>
  typeof v === "string" && v.trim() === "" ? undefined : v;

const OptCleanString = (label, pattern) =>
  z.preprocess(
    emptyToUndef,
    z
      .string()
      .transform((s) => s.trim())
      .optional()
      .refine((s) => s === undefined || s.length > 0, {
        message: `${label} cannot be empty`,
      })
      .refine((s) => s === undefined || !pattern || pattern.test(s), {
        message: `${label} must not contain special characters`,
      })
  );

const OptDate = z.preprocess(
  (v) => (v === "" || v == null ? undefined : v),
  z.coerce
    .date()
    .max(
      new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
      "Owner must be at least 18 years old"
    )
    .optional()
);

// ---- Step2Schema (relevant parts) ----
const OwnerLoose = z.object({
  firstName: OptCleanString("First Name", noSpecialCharactersRegex),
  lastName: OptCleanString("Last Name", noSpecialCharactersRegex),
  address: z.object(
    {
      address1: z
        .string({ required_error: "Please enter a owner Address" })
        .min(1, "Address cannot be empty")
        .regex(
          noSpecialCharactersRegex,
          "Owner Address must not contain special characters"
        ),
      address2: z
        .string()
        .regex(
          noSpecialCharactersRegex,
          "Owner Address 2 must not contain special characters"
        )
        .optional(),
      city: z
        .string({ required_error: "Please enter a owner City" })
        .min(1, "City cannot be empty")
        .regex(
          noSpecialCharactersRegex,
          "Owner City must not contain special characters"
        ),
      state: z.string({ required_error: "Please select a owner State" }),
      zip: z
        .string({ required_error: "Please enter a owner Zip" })
        .regex(/^[0-9]+$/, "Owner ZIP must contain only numbers")
        .min(5, "Owner ZIP code should have 5 digits")
        .max(5, "Owner ZIP code should have 5 digits"),
    },
    { required_error: "Owner Address Required" }
  ),
  dob: OptDate,
  businessType: OptCleanString("businessType", noSpecialCharactersRegex),
});

export const Step2Schema = z
  .object({
    carrier: z
      .array(z.enum(["Cover Whale", "Progressive", "Nirvana", "Accelerant"]))
      .default([]),
    dot: z
      .object({
        hasUsdot: HasUsdot,
        dotNumber: DotNumber,
      })
      .superRefine((d, ctx) => {
        if (d.hasUsdot === "yes" && d.dotNumber == null) {
          ctx.addIssue({
            code: "custom",
            path: ["dotNumber"],
            message: "DOT Number is required when 'Has USDOT' is Yes",
          });
        }

        if (d.hasUsdot === "no" && d.dotNumber != null) {
          ctx.addIssue({
            code: "custom",
            path: ["dotNumber"],
            message: "DOT Number must be empty when 'Has USDOT' is No",
          });
        }
      }),
    motorCarrierNumber: z.preprocess(
      (v) => (v === "" || v == null ? undefined : v),
      z
        .string()
        .min(6, "Motor Carrier Number must be 6 or 7 digits")
        .max(7, "Motor Carrier Number must be 6 or 7 digits")
        .refine(
          (v) => !v || v === "" || validator.isNumeric(v),
          "Motor Carrier Number must be numeric"
        )
        .optional()
    ),
    hasLossRuns: z.enum(["yes", "no", "na"], {
      required_error: "Please select Yes or No",
    }).refine((v) => v !== "na", {
      message: "Please select Yes or No",
    }),
    insuredIssuedWithInLastSixtyDays: z
      .enum(["yes", "no", "na"], {
        required_error: "Please select Yes or No",
      })
      .default("na"),
    insuredName: z
      .string({ required_error: "Insured Name Required" })
      .min(1, "Insured Name cannot be empty"),
    dba: z
      .string()
      .regex(
        noSpecialCharactersRegex,
        "DBA name must not contain special characters"
      )
      .optional(),
    fein: z
      .string()
      .refine(
        (v) => !v || v === "" || validator.isTaxID(v),
        "FEIN must be in valid format"
      ).optional(),
    revenue: z.object({
      projected: z
        .number("Projected Revenue must be a number")
        .min(0, "Projected Revenue cannot be negative"),
      current: z
        .number("Current Revenue must be a number")
        .min(0, "Current Revenue cannot be negative"),
      firstPrior: z
        .number("1st Prior Revenue must be a number")
        .min(0, "1st Prior Revenue cannot be negative"),
      secondPrior: z
        .number("2nd Prior Revenue must be a number" )
        .min(0, "2nd Prior Revenue cannot be negative"),
    }).optional(),
    miles: z.object({
      projected: z
        .number("Projected Miles must be a number")
        .min(0, "Projected Miles cannot be negative"),
      current: z
        .number("Current Miles must be a number")
        .min(0, "Current Miles cannot be negative"),
      firstPrior: z
        .number("1st Prior Miles must be a number")
        .min(0, "1st Prior Miles cannot be negative"),
      secondPrior: z
        .number("2nd Prior Miles must be a number" )
        .min(0, "2nd Prior Miles cannot be negative"),
    }).optional(),
    mailingAddress: z.object(
      {
        address1: z
          .string({ required_error: "Please enter a mailing Address" })
          .min(1, "Address cannot be empty")
          .regex(
            noSpecialCharactersRegex,
            "Mailing Address must not contain special characters"
          ),
        address2: z
          .string()
          .regex(
            noSpecialCharactersRegex,
            "Mailing Address 2 must not contain special characters"
          )
          .optional(),
        city: z
          .string({ required_error: "Please enter a mailing City" })
          .min(1, "City cannot be empty")
          .regex(
            noSpecialCharactersRegex,
            "Mailing City must not contain special characters"
          ),
        state: z.string({ required_error: "Please select a mailing State" }),
        zip: z
          .string({ required_error: "Please enter a mailing Zip" })
          .regex(/^[0-9]+$/, "Mailing ZIP must contain only numbers")
          .min(5, "Mailing ZIP code should have 5 digits")
          .max(5, "Mailing ZIP code should have 5 digits"),
      },
      { required_error: "Mailing Address Required" }
    ),
    garagingAddress: z.object(
      {
        address1: z
          .string({ required_error: "Please enter a mailing Address" })
          .min(1, "Address cannot be empty")
          .regex(
            noSpecialCharactersRegex,
            "Mailing Address must not contain special characters"
          ),
        address2: z
          .string()
          .regex(
            noSpecialCharactersRegex,
            "Mailing Address 2 must not contain special characters"
          )
          .optional(),
        city: z
          .string({ required_error: "Please enter a mailing City" })
          .min(1, "City cannot be empty")
          .regex(
            noSpecialCharactersRegex,
            "Mailing City must not contain special characters"
          ),
        state: z.string({ required_error: "Please select a mailing State" }),
        zip: z
          .string({ required_error: "Please enter a mailing Zip" })
          .regex(/^[0-9]+$/, "Mailing ZIP must contain only numbers")
          .min(5, "Mailing ZIP code should have 5 digits")
          .max(5, "Mailing ZIP code should have 5 digits"),
      },
      { required_error: "Garaging Address Required" }
    ),
    garagingSameAsMailing: z.boolean().optional(),
    insuredContact: z.object(
      {
        firstName: z
          .string({ required_error: "Please enter contact's first name" })
          .min(1, "First Name cannot be empty")
          .regex(
            noSpecialCharactersRegex,
            "First Name must not contain special characters"
          ),
        lastName: z
          .string({ required_error: "Please enter contact's last name" })
          .min(1, "Last Name cannot be empty")
          .regex(
            noSpecialCharactersRegex,
            "Last Name must not contain special characters"
          ),
        email: z
          .email({
            required_error: "Please enter contact's e-mail",
            error: "Please enter a valid e-mail",
          })
          .min(1, "Email cannot be empty"),
        phone: z.preprocess(
          (val) => String(val).replace(/\D/g, ""),
          z
            .string()
            .regex(/^\d{10}$/, "Phone number must be exactly 10 digits")
        )
      },
      { required_error: "Insured Contact Info Required" }
    ),
    owner: OwnerLoose.optional(),
  })
  .superRefine((data, ctx) => {
    const carriers = Array.isArray(data?.carrier) ? data.carrier : [];
    const progressiveOnly =
      carriers.length > 0 && carriers.every((c) => c === "Progressive");

    if (!progressiveOnly && !data?.dot?.hasUsdot) {
      ctx.addIssue({
        code: "custom",
        path: ["dot", "hasUsdot"],
        message: "Please select Yes or No",
      });
    }

    const zipChecks = [
      {
        state: data?.mailingAddress?.state,
        zip: data?.mailingAddress?.zip,
        path: ["mailingAddress", "zip"],
      },
      {
        state: data?.garagingAddress?.state,
        zip: data?.garagingAddress?.zip,
        path: ["garagingAddress", "zip"],
      },
    ];

    zipChecks.forEach(({ state, zip, path }) => {
      if (!isZipCodeValidForState(state, zip)) {
        ctx.addIssue({
          code: "custom",
          path,
          message: "Combination of State and ZIP Code is incorrect",
        });
      }
    });

    const o = data.owner ?? {};

    const present = !!(o.firstName ?? o.lastName ?? o.address ?? o.dob);

    const missing = (v) =>
      v === undefined || (typeof v === "string" && v.trim() === "");

    const requiresMotorCarrier = data.carrier?.includes("Progressive");
    const mcEmpty =
      data.motorCarrierNumber === undefined ||
      (typeof data.motorCarrierNumber === "string" && data.motorCarrierNumber.trim() === "");

    if (requiresMotorCarrier && mcEmpty) {
      ctx.addIssue({
        code: "custom",
        path: ["motorCarrierNumber"],
        message: "Motor Carrier Number is required for this carrier",
      });
    }
    if (missing(o.firstName))
      ctx.addIssue({
        code: "custom",
        path: ["owner", "firstName"],
        message: "First Name is required",
      });
    if (missing(o.lastName))
      ctx.addIssue({
        code: "custom",
        path: ["owner", "lastName"],
        message: "Last Name is required",
      });
    if (missing(o.address))
      ctx.addIssue({
        code: "custom",
        path: ["owner", "address"],
        message: "Address is required",
      });
    if (!o.dob)
      ctx.addIssue({
        code: "custom",
        path: ["owner", "dob"],
        message: "DOB is required",
      });
    if (missing(o.businessType) && data.carrier.includes("Progressive"))
      ctx.addIssue({
        code: "custom",
        path: ["owner", "businessType"],
        message: "Business Type is required",
      });
    if (present) {
      // If user typed something even when not required, validate the content (formatting etc.)
      const full = z.object({
        firstName: z
          .string()
          .min(1)
          .regex(
            noSpecialCharactersRegex,
            "First Name must not contain special characters"
          ),
        lastName: z
          .string()
          .min(1)
          .regex(
            noSpecialCharactersRegex,
            "Last Name must not contain special characters"
          ),
        address: z.object({
          address1: z
            .string({ required_error: "Please enter a owner Address" })
            .min(1, "Address cannot be empty")
            .regex(
              noSpecialCharactersRegex,
              "Owner Address must not contain special characters"
            ),
          address2: z
            .string()
            .regex(
              noSpecialCharactersRegex,
              "Owner Address 2 must not contain special characters"
            )
            .optional(),
          city: z
            .string({ required_error: "Please enter a owner City" })
            .min(1, "City cannot be empty")
            .regex(
              noSpecialCharactersRegex,
              "Owner City must not contain special characters"
            ),
          state: z.string({ required_error: "Please select a owner State" }),
          zip: z
            .string({ required_error: "Please enter a owner Zip" })
            .regex(/^[0-9]+$/, "Owner ZIP must contain only numbers")
            .min(5, "Owner ZIP code should have 5 digits")
            .max(5, "Owner ZIP code should have 5 digits"),
        }),
        dob: z
          .date()
          .max(
            new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
            "Owner must be at least 18 years old"
          ),
      });
      const r = full.safeParse({
        firstName: o.firstName,
        lastName: o.lastName,
        address: o.address,
        dob: o.dob,
      });
      if (!r.success) r.error.issues.forEach((i) => ctx.addIssue(i));
      if (data.insuredIssuedWithInLastSixtyDays === "na" && data.carrier.includes('Progressive')) {
        ctx.addIssue({
          code: "custom",
          path: ["insuredIssuedWithInLastSixtyDays"],
          message: "Please select Yes or No",
        });
      }
      if ((!data.fein || data.fein === "") && data.carrier.includes('Accelerant')) {
        ctx.addIssue({
          code: "custom",
          path: ["fein"],
          message: "Please enter a FEIN",
        });
      }
      if (data.revenue === undefined && data.carrier.includes('Accelerant')) {
        ctx.addIssue({
          code: "custom",
          path: ["revenue"],
          message: "Please enter revenue details",
        });
      }
      if (data.miles === undefined && data.carrier.includes('Accelerant')) {
        ctx.addIssue({
          code: "custom",
          path: ["revenue"],
          message: "Please enter mile details",
        });
      }
    }
  });
